import React, { useState, useEffect, useRef } from 'react';

const AddFAQModal = ({ isOpen, onClose, onAdd, editingFAQ, defaultOrder = 0 }) => {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [order, setOrder] = useState('0');
  const modalRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    if (editingFAQ) {
      setQuestion(editingFAQ.question || '');
      setAnswer(editingFAQ.answer || '');
      const o = editingFAQ.order;
      setOrder(String(o !== undefined && o !== null ? o : 0));
    } else {
      setQuestion('');
      setAnswer('');
      setOrder(String(defaultOrder ?? 0));
    }
  }, [editingFAQ, isOpen, defaultOrder]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.body.style.overflow = 'unset';
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.body.style.overflow = 'unset';
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleSubmit = () => {
    if (!question.trim() || !answer.trim()) {
      alert('Please fill in both Question and Answer');
      return;
    }

    const parsedOrder = parseInt(String(order).trim(), 10);
    const orderNum = Number.isFinite(parsedOrder) ? parsedOrder : 0;

    onAdd({ question, answer, order: orderNum });
    
    // Reset form
    setQuestion('');
    setAnswer('');
    setOrder('0');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div ref={modalRef} className="bg-white rounded-2xl w-full max-w-3xl shadow-2xl">
        {/* Modal Content */}
        <div className="p-10">
          {/* Header */}
          <div className="flex items-center justify-between mb-10">
            <h2 className="text-2xl font-semibold text-gray-900">
              {editingFAQ ? "Edit FAQ's" : "Add FAQ's"}
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>

          {/* Form Content */}
          <div className="space-y-8">
            {/* Display order */}
            <div>
              <label className="block text-sm font-normal text-gray-700 mb-2">
                Display order
              </label>
              <input
                type="number"
                min={0}
                step={1}
                value={order}
                onChange={(e) => setOrder(e.target.value)}
                className="w-full max-w-[200px] px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 transition-all"
                placeholder="0"
              />
              <p className="mt-1 text-xs text-gray-500">Lower numbers appear first in the FAQ list.</p>
            </div>

            {/* Question Field */}
            <div>
              <label className="block text-sm font-normal text-gray-700 mb-2">
                Question
              </label>
              <input
                type="text"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 transition-all"
                placeholder=""
              />
            </div>

            {/* Answer Field */}
            <div>
              <label className="block text-sm font-normal text-gray-700 mb-2">
                Answer
              </label>
              <textarea
                rows={6}
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 transition-all resize-none"
                placeholder=""
              />
            </div>
          </div>

          {/* Footer Buttons */}
          <div className="flex justify-end gap-4 mt-10">
            <button
              onClick={onClose}
              className="px-6 py-3 border border-gray-300 text-gray-700 text-base font-medium rounded-xl hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="px-6 py-3 bg-emerald-400 text-white text-base font-medium rounded-xl hover:bg-emerald-500 transition-colors shadow-sm"
            >
              {editingFAQ ? 'Update FAQ' : 'Add FAQ'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddFAQModal;

